import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Sparkles, Send, Loader2, ChevronDown, ChevronUp, Database } from "lucide-react";
import { toast } from "sonner";
import VoiceMic from "@/components/VoiceMic";

export default function AskRayFuel({ vehicleId = null, className = "" }) {
  const [turns, setTurns] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [openRecords, setOpenRecords] = useState({});
  const [chips, setChips] = useState([]);
  const listRef = useRef(null);

  useEffect(() => {
    const url = vehicleId ? `/ai/history?vehicle_id=${vehicleId}&limit=8` : `/ai/history?limit=8`;
    api.get(url).then(({ data }) => setTurns(data)).catch(() => {});
    api.get("/ai/suggested-questions").then(({ data }) => setChips(data.questions || [])).catch(() => setChips([]));
  }, [vehicleId]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [turns, loading]);

  const ask = async (question) => {
    const text = (question || "").trim();
    if (!text || loading) return;
    setLoading(true);
    setQ("");
    const tempId = `t-${Date.now()}`;
    setTurns((prev) => [...prev, { id: tempId, question: text, answer: null, records_used: [] }]);
    try {
      const payload = { question: text };
      if (vehicleId) payload.vehicle_id = vehicleId;
      const { data } = await api.post("/ai/ask", payload);
      setTurns((prev) => prev.map((t) => (t.id === tempId ? data : t)));
    } catch (e) {
      setTurns((prev) => prev.filter((t) => t.id !== tempId));
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onVoiceResult = (data) => {
    // Voice endpoint already runs the full ask flow and returns { transcript, answer, records_used, ... }
    if (!data) return;
    const t = {
      id: data.id || `v-${Date.now()}`,
      question: data.question || data.transcript || "Voice question",
      answer: data.answer || null,
      records_used: data.records_used || [],
    };
    setTurns((prev) => [...prev, t]);
  };

  return (
    <div className={`border border-[var(--border-line)] bg-[var(--bg-surface)] rounded-xl ${className}`} data-testid="ask-rayfuel-panel">
      <div className="px-4 py-3 border-b border-[var(--border-line)] flex items-center gap-2">
        <Sparkles size={18} className="text-[#FF5A1F]" />
        <span className="font-display text-lg font-black tracking-tight">Ask RayFuel</span>
        <span className="ft-overline text-[10px] text-[var(--text-muted)] ml-auto">
          {vehicleId ? "VEHICLE CONTEXT" : "FLEET CONTEXT"}
        </span>
      </div>

      <div ref={listRef} className="max-h-[400px] overflow-y-auto p-4 space-y-4" data-testid="ask-rayfuel-thread">
        {turns.length === 0 && !loading && (
          <div className="text-sm text-[var(--text-muted)]">
            Ask by text or voice. Answers cite the records used and never invent numbers.
          </div>
        )}
        {turns.map((t, idx) => (
          <div key={t.id} className="space-y-2" data-testid={`ask-turn-${idx}`}>
            <div className="text-sm text-[var(--text-primary)] font-semibold" data-testid={`ask-turn-question-${idx}`}>
              You: {t.question}
            </div>
            {t.answer === null ? (
              <div className="text-sm text-[var(--text-muted)] flex items-center gap-2">
                <Loader2 className="animate-spin" size={14} /> Thinking…
              </div>
            ) : (
              <>
                <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap leading-relaxed" data-testid={`ask-turn-answer-${idx}`}>
                  {t.answer}
                </div>
                {(t.records_used || []).length > 0 && (
                  <div className="border-l-2 border-[#FF5A1F]/60 pl-3">
                    <button
                      className="text-xs text-[var(--text-muted)] hover:text-[#FF5A1F] flex items-center gap-1"
                      onClick={() => setOpenRecords((s) => ({ ...s, [t.id]: !s[t.id] }))}
                      data-testid={`ask-view-records-${idx}`}
                    >
                      <Database size={12} /> View records used ({t.records_used.length})
                      {openRecords[t.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                    {openRecords[t.id] && (
                      <ul className="mt-2 space-y-1 text-xs font-mono text-[var(--text-muted)]">
                        {t.records_used.slice(0, 20).map((r, i) => (
                          <li key={i} className="truncate">
                            {r.kind} · {(r.id || "").slice(0, 8)} · {r.date || "—"} {r.amount ? `· ₹${r.amount}` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
      </div>

      {chips.length > 0 && (
        <div className="px-4 pb-3 pt-2 border-t border-[var(--border-line)]">
          <div className="ft-overline text-[10px] text-[var(--text-muted)] mb-2">TRY ASKING</div>
          <div className="flex flex-wrap gap-2">
            {chips.map((s) => (
              <button
                key={s}
                onClick={() => ask(s)}
                disabled={loading}
                className="text-xs border border-[var(--border-line)] px-2.5 py-1.5 rounded-full hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors disabled:opacity-50"
                data-testid="ask-suggestion"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); ask(q); }}
        className="border-t border-[var(--border-line)] p-3 flex gap-2 items-center"
      >
        <VoiceMic vehicleId={vehicleId} onResult={onVoiceResult} disabled={loading} />
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Ask about your fleet…"
          disabled={loading}
          className="flex-1 bg-[var(--bg-elevated)] border border-[var(--border-line)] px-3 py-2.5 text-sm rounded-lg focus:outline-none focus:border-[#FF5A1F]"
          data-testid="ask-input"
        />
        <button
          type="submit" disabled={loading || !q.trim()}
          className="bg-[#FF5A1F] hover:bg-[#E04F1A] text-white px-4 py-2 rounded-lg disabled:opacity-50"
          data-testid="ask-send"
        >
          {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
}
